// Common
export type ServiceResult = ServiceLoading | ServiceSuccess | ServiceError;

export interface ServiceLoading {
  loading: true;
}
export function isServiceLoading(val: ServiceResult): val is ServiceLoading {
  return "loading" in val;
}
export interface ServiceSuccess {
  data: ServiceSuccessData;
}
export type ServiceSuccessData =
  | OrionTelemetry
  | ArtemisBlog
  | ArtemisBlogItem
  | ArtemisTimeline
  | LiveEventsSchedule
  | DSNData
  | StreamsData;

export function isServiceSuccess(val: ServiceResult): val is ServiceSuccess {
  return val.hasOwnProperty("data");
}

// Telemetry

export function isOrionTelemetry(
  val: ServiceSuccessData,
): val is OrionTelemetry {
  return val.hasOwnProperty("metadata");
}

export interface ServiceError {
  error: Error;
}
export function isServiceError(val: ServiceResult): val is ServiceError {
  return val.hasOwnProperty("error");
}

// OrionTelemetry
export type OrionTelemetryActivity = "MIS" | "TST";

export interface OrionTelemetryDatapoint {
  name: string;
  description: string;
  time: string | Date;
  unit: string;
  value: string | number | boolean;
}

export type OrionTelemetry =
  | OrionTelemetryAvailable
  | OrionTelemetryServiceNotRunning
  | OrionTelemetryLOS;

export interface OrionTelemetryMetadata {
  timestamp: Date;
  activity: OrionTelemetryActivity;
  type: 4 | 253 | 255; // 4 - Data available, 253 - Service not running, 255 - LOS
}
export interface OrionTelemetryMetadataRaw {
  Date: string;
  Activity: OrionTelemetryActivity;
  Type: 4 | 253 | 255;
}

export interface OrionTelemetryAvailable {
  metadata: OrionTelemetryMetadata;
  data: Array<OrionTelemetryDatapoint>;
}
export function isOrionTelemetryDataAvailable(
  val: OrionTelemetry,
): val is OrionTelemetryAvailable {
  return val.hasOwnProperty("metadata") && val.hasOwnProperty("data");
}
export function isOrionTelemetryAvailable(
  val: OrionTelemetry,
): val is OrionTelemetryAvailable {
  return (
    val.hasOwnProperty("metadata") &&
    val.metadata.type === 4 &&
    val.hasOwnProperty("data")
  );
}
export interface OrionTelemetryServiceNotRunning {
  metadata: OrionTelemetryMetadata;
}
export function isOrionTelemetryServiceNotRunning(
  val: OrionTelemetry,
): val is OrionTelemetryServiceNotRunning {
  return val.hasOwnProperty("metadata") && val.metadata.type === 253;
}
export interface OrionTelemetryLOS {
  metadata: OrionTelemetryMetadata;
  data?: Array<OrionTelemetryDatapoint>;
}
export function isOrionTelemetryLOS(
  val: OrionTelemetry,
): val is OrionTelemetryLOS {
  return val.hasOwnProperty("metadata") && val.metadata.type === 255;
}

export const UNIT_SYSTEM_OPTIONS = ["METRIC", "IMPERIAL", "RAW"] as const;
export type UnitSystemOption = (typeof UNIT_SYSTEM_OPTIONS)[number];
export interface UnitConverters {
  [key: string]: (v: number) => string;
}

// ArtemisTimeline
export interface ArtemisTimeline {
  timestamp: Date;
  data: {
    met: number;
    standard: {
      flightDay: ArtemisTimelineItem[];
      crew: ArtemisTimelineItem[];
      attitude: ArtemisTimelineItem[];
      phase: ArtemisTimelineItem[];
    };
    simple: ArtemisTimelineSimpleItem[];
    milestones: ArtemisTimelineMilestoneItem[];
  };
}
export function isArtemisTimeline(val: Object): val is ArtemisTimeline {
  return (
    "timestamp" in val &&
    typeof val.timestamp === "number" &&
    "data" in val &&
    val.data instanceof Object &&
    "met" in val.data &&
    "standard" in val.data &&
    val.data.standard instanceof Object &&
    "flightDay" in val.data.standard &&
    Array.isArray(val.data.standard.flightDay) &&
    val.data.standard.flightDay.reduce(
      (prev, cur) => (prev ? isArtemisTimelineItem(cur) : false),
      true,
    ) &&
    "crew" in val.data.standard &&
    Array.isArray(val.data.standard.crew) &&
    val.data.standard.crew.reduce(
      (prev, cur) => (prev ? isArtemisTimelineItem(cur) : false),
      true,
    ) &&
    "attitude" in val.data.standard &&
    Array.isArray(val.data.standard.attitude) &&
    val.data.standard.attitude.reduce(
      (prev, cur) => (prev ? isArtemisTimelineItem(cur) : false),
      true,
    ) &&
    "phase" in val.data.standard &&
    Array.isArray(val.data.standard.phase) &&
    val.data.standard.phase.reduce(
      (prev, cur) => (prev ? isArtemisTimelineItem(cur) : false),
      true,
    ) &&
    "simple" in val.data &&
    Array.isArray(val.data.simple) &&
    val.data.simple.reduce(
      (prev, cur) => (prev ? isArtemisTimelineSimpleItem(cur) : false),
      true,
    ) &&
    "milestones" in val.data &&
    Array.isArray(val.data.milestones) &&
    val.data.milestones.reduce(
      (prev, cur) => (prev ? isArtemisTimelineMilestoneItem(cur) : false),
      true,
    )
  );
}

export interface ArtemisTimelineItem {
  start: number;
  end: number;
  title: string;
  category?: string;
  description?: string;
}
export function isArtemisTimelineItem(val: Object): val is ArtemisTimelineItem {
  return (
    "start" in val &&
    typeof val.start === "number" &&
    "end" in val &&
    typeof val.end === "number" &&
    "title" in val &&
    typeof val.title === "string" &&
    ("category" in val ? typeof val.category === "string" : true) &&
    ("description" in val ? typeof val.description === "string" : true)
  );
}

export interface ArtemisTimelineMilestoneItem {
  time: number;
  title: string;
  category: string;
  description: string;
}
export function isArtemisTimelineMilestoneItem(
  val: Object,
): val is ArtemisTimelineMilestoneItem {
  return (
    "time" in val &&
    typeof val.time === "number" &&
    "title" in val &&
    typeof val.title === "string" &&
    "category" in val &&
    typeof val.category === "string" &&
    "description" in val &&
    typeof val.description === "string"
  );
}

export interface ArtemisTimelineSimpleItem {
  name: string;
  description: string;
  color: string;
  timeline: ArtemisTimelineSimpleTimelineItem[];
}
export function isArtemisTimelineSimpleItem(
  val: Object,
): val is ArtemisTimelineSimpleItem {
  return (
    "name" in val &&
    typeof val.name === "string" &&
    "description" in val &&
    typeof val.description === "string" &&
    "color" in val &&
    typeof val.color === "string" &&
    "timeline" in val &&
    Array.isArray(val.timeline) &&
    val.timeline.reduce(
      (prev, cur) => (prev ? isArtemisTimelineSimpleTimelineItem(cur) : false),
      true,
    )
  );
}

export interface ArtemisTimelineSimpleTimelineItem {
  start: number;
  end: number;
  active: boolean;
}
export function isArtemisTimelineSimpleTimelineItem(
  val: Object,
): val is ArtemisTimelineSimpleTimelineItem {
  return (
    "start" in val &&
    typeof val.start === "number" &&
    "end" in val &&
    typeof val.end === "number" &&
    "active" in val &&
    typeof val.active === "boolean"
  );
}

// DSN
export interface DSNData {
  timestamp: number;
  data: {
    dishes: DSNDish[];
  };
}
export function isDSNData(val: Object): val is DSNData {
  return (
    "timestamp" in val &&
    typeof val.timestamp === "number" &&
    "data" in val &&
    val.data instanceof Object &&
    "dishes" in val.data &&
    Array.isArray(val.data.dishes) &&
    val.data.dishes.reduce((prev, cur) => (prev ? isDSNDish(cur) : false), true)
  );
}
export interface DSNDish {
  name: string;
  activity: string;
  azimuthAngle: string; // number
  elevationAngle: string; // number
  windSpeed: string; // number
  isArray: string; // boolean
  isDDOR: string; // boolean
  isMSPA: string; // boolean
  site: {
    name: string;
    friendlyName: string;
    longitude: string; // number
    latitude: string; // number
  };
  target: DSNTarget[];
  downSignal: DSNDishSignal[];
  upSignal: DSNDishSignal[];
}
export function isDSNDish(val: Object): val is DSNDish {
  return (
    [
      "name",
      "activity",
      "azimuthAngle",
      "elevationAngle",
      "windSpeed",
      "isArray",
      "isDDOR",
      "isMSPA",
      "target",
      "downSignal",
      "upSignal",
    ].reduce((prev, cur) => (prev ? cur in val : false), true) &&
    ["name", "friendlyName", "longitude", "latitude"].reduce(
      (prev, cur) =>
        prev
          ? "site" in val && val.site instanceof Object && cur in val.site
          : false,
      true,
    ) &&
    "target" in val &&
    Array.isArray(val.target) &&
    val.target.reduce((prev, cur) => (prev ? isDSNTarget(cur) : false), true) &&
    "downSignal" in val &&
    Array.isArray(val.downSignal) &&
    val.downSignal.reduce(
      (prev, cur) => (prev ? isDSNDishSignal(cur) : false),
      true,
    ) &&
    "upSignal" in val &&
    Array.isArray(val.upSignal) &&
    val.upSignal.reduce(
      (prev, cur) => (prev ? isDSNDishSignal(cur) : false),
      true,
    )
  );
}
export interface DSNDishSignal {
  band: string;
  active: string; // boolean
  dataRate: string; // number
  frequency: string; // number
  power: string; // number
  signalType: string;
  spacecraft: string;
  spacecraftID: string;
}
export function isDSNDishSignal(val: object): val is DSNDishSignal {
  return [
    "band",
    "active",
    "dataRate",
    "frequency",
    "power",
    "signalType",
    "spacecraft",
    "spacecraftID",
  ].reduce((prev, cur) => (prev ? cur in val : false), true);
}
export interface DSNTarget {
  id: string; // number
  name: string;
  rtlt: string; // number
  downlegRange: string; // number
  uplegRange: string; // number
}
export function isDSNTarget(val: object): val is DSNTarget {
  return ["id", "name", "rtlt", "downlegRange", "uplegRange"].reduce(
    (prev, cur) => (prev ? cur in val : false),
    true,
  );
}

// ArtemisBlog
export interface ArtemisBlog {
  timestamp: Date;
  data: {
    metadata: {
      title: string;
      link: string;
      description: string;
      lastBuildDate: Date;
      language: string;
      generator: string;
    };
    entries: ArtemisBlogItem[];
  };
}
export function isArtemisBlog(val: ServiceSuccessData): val is ArtemisBlog {
  return (
    "timestamp" in val &&
    "data" in val &&
    "metadata" in val.data &&
    ["title", "link", "description", "lastBuildDate"].reduce(
      (prev, key) =>
        prev
          ? Object.keys(
              "metadata" in val.data ? val.data.metadata || {} : {},
            ).includes(key)
          : false,
      true,
    ) &&
    "entries" in val.data &&
    Array.isArray(val.data.entries) &&
    val.data.entries.reduce(
      (prev: boolean, entry: Object) =>
        prev ? isArtemisBlogItem(entry) : false,
      true,
    )
  );
}

export interface ArtemisBlogItem {
  title: string;
  link: string;
  creator: string;
  published: string;
  description: string;
  content: string;
  titleImageSource: string;
}
export function isArtemisBlogItem(val: unknown): val is ArtemisBlogItem {
  return [
    "title",
    "link",
    "creator",
    "published",
    "description",
    "content",
    "titleImageSource",
  ].reduce(
    (prev, key) => (prev ? Object.keys(val || {}).includes(key) : false),
    true,
  );
}

// LiveEventsSchedule
export interface LiveEventsScheduleEntry {
  content: string;
  streaming_services: { [key: string]: string };
  time: Date;
}
export interface LiveEventsScheduleDay {
  day: string;
  events: Array<LiveEventsScheduleEntry>;
}
export interface LiveEventsSchedule {
  data: {
    schedule: LiveEventsScheduleEntry[];
  };
  effective: Date;
  timestamp: Date;
}
export function isLiveEventsSchedule(
  val: ServiceSuccessData,
): val is LiveEventsSchedule {
  return (
    "timestamp" in val &&
    "effective" in val &&
    "data" in val &&
    "schedule" in val.data &&
    val.data.schedule.reduce(
      (prev: boolean, entry: Object) =>
        prev
          ? "content" in entry &&
            "streaming_services" in entry &&
            "time" in entry
          : false,
      true,
    )
  );
}

// Streams
export interface Stream {
  id: string;
  title: string;
  thumbnail: {
    thumbnails: Array<string>;
  };
}
export interface StreamsData {
  timestamp: Date;
  data: { [key: string]: Stream };
}
export function isStreamsData(val: ServiceSuccessData): val is StreamsData {
  return "timestamp" in val && "data" in val;
}
